/*
 * Purpose: Record live Mission actions — dossier + task receipt + map trail + DB retain
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import type { MissionDossierEntry } from '@/services/mission-dossier/types';
import type { MissionMapReceiptDelta } from '@/services/mission-receipts/types';
import type { MissionStepKind } from '@/services/autoblocks/missionTypes';
import { createMissionStep } from '@/services/autoblocks/missionTypes';
import {
  useMissionDossierStore,
  type RecordMissionDossierInput,
} from '@/stores/missionDossierStore';
import { useMissionTaskReceiptStore } from '@/stores/missionTaskReceiptStore';
import { retainMissionGeneratedAsync } from '@/services/mission-knowledge/persistGenerated';
import { MISSION_LIVE_DOCUMENTATION_RULE } from '@/lib/missionLiveDocumentationPolicy';
import { notifyMissionTaskReceipt } from '@/lib/missionNotifications';
import { useMissionNotificationStore } from '@/stores/missionNotificationStore';

export type RecordMissionLiveActionInput = RecordMissionDossierInput & {
  /** Receipt title — defaults to summary */
  receiptTitle?: string;
  /** Push a step onto the Mission map trail UI */
  mapTrail?: boolean | { kind?: MissionStepKind; label?: string; detail?: string };
  /** Document a dynamic map mutation on this receipt */
  mapDelta?: MissionMapReceiptDelta;
  /** Show toast notification (default: true for ai/system) */
  notify?: boolean;
};

const DOSSIER_TO_TRAIL: Partial<Record<string, MissionStepKind>> = {
  mission_started: 'capture',
  outcome_edited: 'capture',
  interpret: 'interpret',
  confirm: 'confirm',
  mode_switch: 'mode_switch',
  plan: 'plan',
  director: 'plan',
  discovery: 'plan',
  connector_built: 'plan',
  dry_run: 'dry_run',
  approve: 'approve',
  live_run: 'approve',
  blocker: 'blocker',
  express: 'express',
  error: 'blocker',
  automation_consent: 'confirm',
  risk_approval: 'approve',
  human_action: 'confirm',
  task_receipt: 'plan',
  map_patch: 'plan',
};

function receiptTitleFor(input: RecordMissionLiveActionInput): string {
  if (input.receiptTitle?.trim()) return input.receiptTitle.trim();
  return input.summary.trim().slice(0, 120);
}

async function pushMissionMapTrail(
  input: RecordMissionLiveActionInput,
  detail?: string
): Promise<void> {
  if (input.mapTrail === false) return;

  const custom =
    input.mapTrail && typeof input.mapTrail === 'object' ? input.mapTrail : undefined;
  const kind =
    custom?.kind ?? DOSSIER_TO_TRAIL[input.kind] ?? (input.actor === 'ai' ? 'plan' : 'confirm');
  const label = custom?.label ?? input.summary.trim().slice(0, 80);
  const stepDetail = custom?.detail ?? detail ?? input.action;

  const { useAutoBlocksStore } = await import('@/stores/autoBlocksStore');
  useAutoBlocksStore
    .getState()
    .appendMissionTrail(createMissionStep(kind, label, stepDetail?.slice(0, 200)));
}

/**
 * Single entry point for live Mission documentation (dossier + receipt + trail + DB).
 * Open-ended AI is acceptable when this is called for every action.
 */
export function recordMissionLiveAction(input: RecordMissionLiveActionInput): MissionDossierEntry | null {
  const dossierEntry = useMissionDossierStore.getState().append(input);
  if (!dossierEntry) return null;

  const missionSessionId = dossierEntry.missionSessionId;
  const receipt = useMissionTaskReceiptStore.getState().append({
    missionSessionId,
    dossierEntryId: dossierEntry.id,
    actor: dossierEntry.actor,
    kind: dossierEntry.kind,
    title: receiptTitleFor(input),
    summary: dossierEntry.summary,
    mapDelta: input.mapDelta,
  });

  void pushMissionMapTrail(input, dossierEntry.reasoning ?? dossierEntry.action);

  if (missionSessionId && receipt) {
    retainMissionGeneratedAsync(missionSessionId, 'task_receipt', {
      receiptId: receipt.id,
      seq: receipt.seq,
      dossierEntryId: dossierEntry.id,
      kind: dossierEntry.kind,
      actor: dossierEntry.actor,
      title: receipt.title,
      summary: receipt.summary,
      mapDelta: receipt.mapDelta,
      policy: MISSION_LIVE_DOCUMENTATION_RULE,
    });

    useMissionNotificationStore.getState().onNewReceipt(receipt.seq);
    if (dossierEntry.kind === 'plan' || dossierEntry.kind === 'map_patch' || dossierEntry.kind === 'director') {
      useMissionNotificationStore.getState().onPlanUpdated();
    }

    const shouldNotify =
      input.notify !== false &&
      (input.notify === true ||
        dossierEntry.actor === 'ai' ||
        dossierEntry.actor === 'system' ||
        dossierEntry.kind === 'error');
    if (shouldNotify) {
      notifyMissionTaskReceipt(receipt, dossierEntry.kind, dossierEntry.actor);
    }
  }

  return dossierEntry;
}

/** Alias — all Mission audit should go through live documentation channels. */
export function recordMissionDossier(input: RecordMissionLiveActionInput): MissionDossierEntry | null {
  return recordMissionLiveAction(input);
}

/** Document a dynamic Mission map patch with full live audit channels. */
export function recordMissionMapPatchLive(input: {
  rationale: string;
  mapDelta: MissionMapReceiptDelta;
  summary?: string;
}): MissionDossierEntry | null {
  return recordMissionLiveAction({
    actor: 'ai',
    kind: 'map_patch',
    summary: input.summary ?? `Mission map updated — ${input.rationale}`.slice(0, 200),
    action: input.rationale,
    payload: input.mapDelta as Record<string, unknown>,
    mapDelta: input.mapDelta,
    mapTrail: { kind: 'plan', label: 'Map updated', detail: input.rationale },
    receiptTitle: 'Mission map patch',
  });
}
